/**
 * Workshop version of TodoFrame — Denker list-row style checklist.
 *
 * Row recipe copied verbatim from taskboard-frame/list-view-row
 * (transparent rows, priority accent bar, no pill tags). Denker glass
 * outside. No divider lines between rows, no status dots in content.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import { surfaceRoleAttributes, surfaceRoleClassName } from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TodoItem {
  id: string;
  text: string;
  detail?: string;
  priority?: "urgent" | "high" | "medium" | "low";
  done?: boolean;
}

interface TodoFrameProps {
  items?: TodoItem[];
  status?: FrameStatus;
  onToggle?: (id: string) => void;
  className?: string;
  "data-testid"?: string;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_ITEMS: TodoItem[] = [
  { id: "1", text: "Book flights to NYC", detail: "Fri evening · JFK", priority: "urgent" },
  { id: "2", text: "Confirm hotel near Williamsburg", detail: "2 nights", priority: "high", done: true },
  { id: "3", text: "Find rooftop bar for Thursday", priority: "medium" },
  { id: "4", text: "Pack chargers + passport", priority: "low" },
];

// ─── Priority accent bar ──────────────────────────────────────────────────────
// Copied verbatim from taskboard-frame/task-metadata-chips.tsx
// (PRIORITY_BORDER_CLASSES). Workshop boundary: do not import production
// files — the class strings are duplicated here intentionally.

const PRIORITY_BORDER_CLASSES: Record<string, string> = {
  urgent: "border-l-danger",
  high: "border-l-warning",
  medium: "border-l-status-selected",
  low: "border-l-muted",
};

// ─── Row ──────────────────────────────────────────────────────────────────────

function TodoRow({
  item,
  checked,
  onToggle,
  testId,
}: {
  item: TodoItem;
  checked: boolean;
  onToggle: () => void;
  testId: string;
}) {
  const borderClass =
    PRIORITY_BORDER_CLASSES[item.priority ?? ""] ?? "border-l-transparent";

  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-control px-4 py-1.5 transition-colors",
        "cursor-pointer hover:bg-glass-stroke-faint/20",
        checked && "opacity-40",
      )}
      onClick={onToggle}
      data-testid={`${testId}-row-${item.id}`}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={`Mark "${item.text}" ${checked ? "not done" : "done"}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        data-testid={`${testId}-toggle-${item.id}`}
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
          checked
            ? "border-accent bg-accent text-black"
            : "border-muted/50 text-transparent hover:border-accent",
        )}
      >
        <Icons.Check className="h-2.5 w-2.5" aria-hidden="true" />
      </button>
      {/* Priority accent bar */}
      <div
        className={cn(
          "w-[3px] shrink-0 self-stretch rounded-full border-l-2",
          borderClass,
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-appkit-caption text-secondary",
            checked && "line-through text-muted",
          )}
        >
          {item.text}
        </span>
        {item.detail && (
          <p className="mt-0.5 truncate text-canvas-xs text-muted">{item.detail}</p>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function TodoFrame({
  items = MOCK_ITEMS,
  status = "done",
  onToggle,
  className,
  "data-testid": testId = "todo-frame",
}: TodoFrameProps) {
  const [localDone, setLocalDone] = useState<Record<string, boolean>>({});

  function isChecked(item: TodoItem): boolean {
    return localDone[item.id] ?? item.done ?? false;
  }

  function handleToggle(id: string) {
    setLocalDone((prev) => ({ ...prev, [id]: !(prev[id] ?? items.find((i) => i.id === id)?.done ?? false) }));
    onToggle?.(id);
  }

  const doneCount = items.filter(isChecked).length;

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex flex-col overflow-hidden rounded-frame",
        className,
      )}
      {...surfaceRoleAttributes("frame", { nativeLevel: "root" })}
      data-testid={testId}
    >
      <div className="flex flex-1 flex-col gap-1 overflow-auto px-2 py-2">
        {status === "error" ? (
          <div className="flex items-center gap-2 px-1 py-3" data-testid={`${testId}-error`}>
            <Icons.CircleAlert className="h-3.5 w-3.5 shrink-0 text-status-error" aria-hidden="true" />
            <span className="text-canvas-base text-muted">Couldn&apos;t load tasks. Try again.</span>
          </div>
        ) : status === "streaming" && items.length === 0 ? (
          <div className="flex items-center gap-2 px-1 py-3" data-testid={`${testId}-loading`}>
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">Gathering tasks…</span>
          </div>
        ) : items.length === 0 ? (
          <p className="px-1 py-3 text-center text-canvas-base italic text-muted" data-testid={`${testId}-empty`}>
            No tasks yet
          </p>
        ) : (
          <>
            <span className="px-1 text-canvas-xs text-muted" data-testid={`${testId}-progress`}>
              {doneCount} of {items.length} done
            </span>
            <div className="flex flex-col gap-0.5">
              {items.map((item) => (
                <TodoRow
                  key={item.id}
                  item={item}
                  checked={isChecked(item)}
                  onToggle={() => handleToggle(item.id)}
                  testId={testId}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export type { TodoFrameProps, TodoItem };
